/**
 * Custom theme scripts.
 *
 * IMPLEMENTATION NOTE: this file is intentionally kept outside of the Vite build so it survives
 * theme rebuilds. See layout/theme.liquid for where it gets loaded.
 */

/**
 * Order quantity limit
 * ------------------------------------------------------------------------------------------------
 * Prevents a customer from adding to cart, or increasing a line item quantity, past the maximum
 * number of total units allowed per order (summed across every product/variant in the cart).
 * Configurable from Theme settings > Cart > "Order quantity limit".
 */
(() => {
  const settings = window.themeVariables?.settings || {};
  const strings = window.themeVariables?.strings || {};
  const routes = window.themeVariables?.routes || {};

  const ENABLED = Boolean(settings.orderQuantityLimitEnabled);
  const MAX_QUANTITY = Number.parseInt(settings.orderQuantityLimitMax, 10);
  const MESSAGE_HTML = strings.orderQuantityLimitMessage || '';

  if (!ENABLED || !Number.isFinite(MAX_QUANTITY) || MAX_QUANTITY <= 0) {
    return;
  }

  const BANNER_ID = 'order-quantity-limit-banner';
  const BANNER_STYLE_ID = `${BANNER_ID}-styles`;
  const BANNER_AUTO_HIDE_MS = 8000;

  let cartItemCount = 0;
  let cartLines = [];

  const ingestCart = (cart) => {
    if (!cart) return;

    if (typeof cart.item_count === 'number') {
      cartItemCount = cart.item_count;
    }

    if (Array.isArray(cart.items)) {
      cartLines = cart.items.map((item, index) => ({
        key: item.key,
        id: item.id,
        variantId: item.variant_id,
        line: index + 1,
        quantity: item.quantity
      }));
    }
  };

  const refreshCartFromServer = async () => {
    try {
      const cartUrl = routes.cartUrl || '/cart';
      const response = await fetch(`${cartUrl}.js`, { credentials: 'same-origin' });
      if (response.ok) {
        ingestCart(await response.json());
      }
    } catch (error) {
      console.warn('[order-quantity-limit] Unable to fetch current cart state', error);
    }
  };

  refreshCartFromServer();

  ['cart:init', 'cart:refresh', 'cart:updated'].forEach((eventName) => {
    document.documentElement.addEventListener(eventName, (event) => {
      ingestCart(event.detail?.cart);
    });
  });

  const injectBannerStyles = () => {
    if (document.getElementById(BANNER_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = BANNER_STYLE_ID;
    style.textContent = `
      #${BANNER_ID} {
        position: fixed;
        top: 16px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 9999;
        max-width: min(92vw, 480px);
        background: rgb(var(--error-background, 253, 235, 235));
        color: rgb(var(--error-color, 222, 42, 42));
        border: 1px solid rgb(var(--error-color, 222, 42, 42));
        border-radius: 4px;
        padding: 14px 40px 14px 16px;
        font-size: 14px;
        line-height: 1.4;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
      }

      #${BANNER_ID} p {
        margin: 0;
      }

      #${BANNER_ID} a {
        color: inherit;
        text-decoration: underline;
      }

      #${BANNER_ID}__close {
        position: absolute;
        top: 6px;
        right: 8px;
        background: transparent;
        border: 0;
        font-size: 18px;
        line-height: 1;
        cursor: pointer;
        color: inherit;
        padding: 4px;
      }
    `;
    document.head.appendChild(style);
  };

  const showLimitBanner = () => {
    injectBannerStyles();

    let banner = document.getElementById(BANNER_ID);

    if (!banner) {
      banner = document.createElement('div');
      banner.id = BANNER_ID;
      banner.setAttribute('role', 'alert');

      const content = document.createElement('div');
      content.innerHTML = MESSAGE_HTML;

      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.id = `${BANNER_ID}__close`;
      closeButton.setAttribute('aria-label', strings.accessibilityClose || 'Close');
      closeButton.innerHTML = '&times;';
      closeButton.addEventListener('click', () => banner.remove());

      banner.appendChild(content);
      banner.appendChild(closeButton);
      document.body.appendChild(banner);
    }

    clearTimeout(banner._hideTimeout);
    banner._hideTimeout = window.setTimeout(() => banner.remove(), BANNER_AUTO_HIDE_MS);
  };

  const findCartLine = ({ line, id }) => {
    if (line) {
      return cartLines.find((cartLine) => cartLine.line === Number(line)) || null;
    }

    if (id) {
      return (
        cartLines.find(
          (cartLine) =>
            String(cartLine.key) === String(id) ||
            String(cartLine.id) === String(id) ||
            String(cartLine.variantId) === String(id)
        ) || null
      );
    }

    return null;
  };

  const buildBlockedResponse = () => {
    return new Response(
      JSON.stringify({
        status: 422,
        message: 'quantity_not_allowed',
        description: MESSAGE_HTML.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
      }),
      { status: 422, headers: { 'Content-Type': 'application/json' } }
    );
  };

  const parseAddQuantity = async (input, init) => {
    try {
      if (init?.body instanceof FormData) {
        return Number.parseInt(init.body.get('quantity'), 10) || 1;
      }

      if (typeof init?.body === 'string') {
        const payload = JSON.parse(init.body);

        if (Array.isArray(payload.items)) {
          return payload.items.reduce((sum, item) => sum + (Number.parseInt(item.quantity, 10) || 1), 0);
        }

        if (typeof payload.quantity !== 'undefined') {
          return Number.parseInt(payload.quantity, 10) || 1;
        }

        return 1;
      }
    } catch (error) {
      console.warn('[order-quantity-limit] Unable to parse add-to-cart request', error);
    }

    return null;
  };

  const parseChangePayload = (init) => {
    if (typeof init?.body !== 'string') return null;

    try {
      return JSON.parse(init.body);
    } catch (error) {
      return null;
    }
  };

  const willExceedLimitOnChange = (payload) => {
    if (!payload) return false;

    if (payload.updates && typeof payload.updates === 'object') {
      let projectedTotal = cartItemCount;

      Object.entries(payload.updates).forEach(([id, quantity]) => {
        const line = findCartLine({ id });
        const previousQuantity = line ? line.quantity : 0;
        projectedTotal += Number(quantity) - previousQuantity;
      });

      return projectedTotal > cartItemCount && projectedTotal > MAX_QUANTITY;
    }

    if (typeof payload.quantity !== 'number') return false;

    const line = findCartLine(payload);
    const previousQuantity = line ? line.quantity : 0;
    const nextQuantity = payload.quantity;

    if (nextQuantity <= previousQuantity) return false;

    return cartItemCount - previousQuantity + nextQuantity > MAX_QUANTITY;
  };

  // Layer 1: block "Add to cart" submissions before the theme's own handler ever sees them.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.action || !form.action.includes('/cart/add')) return;

      const formData = new FormData(form);
      const quantity = Number.parseInt(formData.get('quantity'), 10) || 1;

      if (cartItemCount + quantity > MAX_QUANTITY) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showLimitBanner();
      }
    },
    true
  );

  // Layer 2: network-level safety net for add-to-cart/quantity-change requests that do not go
  // through a native form submission (e.g. the cart page quantity stepper).
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    try {
      const url = typeof input === 'string' ? input : input?.url || '';
      const method = (init?.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
      const pathname = new URL(url, window.location.origin).pathname;

      if (method === 'POST' && pathname.includes('/cart/add')) {
        const quantity = await parseAddQuantity(input, init);
        if (quantity !== null && cartItemCount + quantity > MAX_QUANTITY) {
          showLimitBanner();
          return buildBlockedResponse();
        }
      }

      if (method === 'POST' && pathname.includes('/cart/change')) {
        const payload = parseChangePayload(init);
        if (willExceedLimitOnChange(payload)) {
          showLimitBanner();
          return buildBlockedResponse();
        }
      }
    } catch (error) {
      console.warn('[order-quantity-limit] fetch guard error', error);
    }

    const response = await originalFetch(input, init);

    try {
      const url = typeof input === 'string' ? input : input?.url || '';
      const pathname = new URL(url, window.location.origin).pathname;

      if (response.ok && (pathname.includes('/cart/add') || pathname.includes('/cart/change') || pathname.includes('/cart/update'))) {
        response
          .clone()
          .json()
          .then((data) => {
            if (data && typeof data.item_count === 'number') {
              ingestCart(data);
            } else if (pathname.includes('/cart/add')) {
              // /cart/add.js only returns the added line, not the full cart: resync in the background.
              refreshCartFromServer();
            }
          })
          .catch(() => {});
      }
    } catch (error) {
      // Non-blocking: cache resync failures should never affect the real cart response.
    }

    return response;
  };
})();
